import moment from 'moment';

import type { FilterConfig } from '../types';

import { FilterType } from '../types/FilterType';
import { FILTER_TYPES } from '../constants/FilterTypes';
import { FilterCriteriaModel } from '../models/FilterCriteriaModel';

export class DataTableViewModel {
    constructor(private readonly filterConfig: FilterConfig[]) {}

    normalizeDataDates(rawData: any[]): any[] {
        return rawData.map(item => {
            const normalized = { ...item };

            Object.keys(normalized).forEach(key => {
                if (this.isValidDate(normalized[key])) {
                    normalized[key] = moment.utc(normalized[key]).toDate();
                }
            });

            return normalized;
        });
    }

    createFilter(fieldName: string, type: FilterType, value: any) {
        const config = this.filterConfig.find(
            c => c.fieldName === fieldName,
        );

        return {
            type,
            filterNameLabel: config?.label,
            value,
        };
    }

    /** Filters without the empty ones (see FilterCriteriaModel.isEmptyValue). */
    cleanFilters(filters: Record<string, any>): Record<string, any> {
        return FilterCriteriaModel.clean(filters);
    }

    enhanceFilterParams(
        fieldName: string,
        type: FilterType,
        filterParams: any,
        searchCriteria: any,
    ): any {
        const filterValue =
            searchCriteria?.filters?.[fieldName];

        switch (type) {
            case FILTER_TYPES.LIST: {
                if (
                    !filterParams ||
                    !Array.isArray(filterParams)
                ) {
                    return [];
                }

                const selected =
                    filterValue?.value ?? [];

                return filterParams.map((item: any) => ({
                    ...item,
                    checked:
                        Array.isArray(selected) &&
                        selected.some(
                            (selectedItem: any) =>
                                selectedItem.id === item.id,
                        ),
                }));
            }

            case FILTER_TYPES.TEXT:
                return filterValue ?? '';

            case FILTER_TYPES.DATE_RANGE:
                return {
                    from:
                        filterValue?.value?.from ??
                        null,
                    to:
                        filterValue?.value?.to ??
                        null,
                    minDate:
                        filterParams?.minDate ??
                        null,
                    maxDate:
                        filterParams?.maxDate ??
                        null,
                };

            case FILTER_TYPES.DATE_TIME_RANGE:
                return (
                    filterValue?.value ?? {
                        from: null,
                        to: null,
                    }
                );

            case FILTER_TYPES.NUMBER_RANGE:
            case FILTER_TYPES.AMOUNT: {
                const config =
                    this.filterConfig.find(
                        c =>
                            c.fieldName ===
                            fieldName,
                    );

                return {
                    ...(
                        filterValue?.value ?? {
                            from: null,
                            to: null,
                        }
                    ),
                    minValue:
                        config?.params?.minValue ??
                        0,
                    maxValue:
                        config?.params?.maxValue ??
                        100,
                };
            }

            case FILTER_TYPES.BOOLEAN:
                return (
                    filterValue?.value !== undefined &&
                    filterValue?.value !== null
                )
                    ? filterValue.value
                    : null;

            default:
                return filterParams;
        }
    }

    private isValidDate(value: any): boolean {
        if (typeof value !== 'string') {
            return false;
        }

        const dateOnly =
            /^\d{4}-\d{2}-\d{2}$/;

        const dateTimeZ =
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/;

        return (
            (
                dateOnly.test(value) ||
                dateTimeZ.test(value)
            ) &&
            moment(
                value,
                moment.ISO_8601,
                true,
            ).isValid()
        );
    }
}